import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MdEdit, MdDelete, MdPeople, MdAdd, MdClose, MdStar, MdCheckCircle } from "react-icons/md";
import Card, { CardHeader } from "components/card";
import Avatar from "components/avatar";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import StatCard from "components/stat-card";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";
import { useConfirm } from "components/dialog/ConfirmProvider";
import groupsService from "services/groupsService";
import { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { statusTone } from "lib/statusTone";
import { formatDate, formatNumber } from "utils/format";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { GroupSwatch, groupRoleLabel, groupTypeLabel } from "./groupDisplay";

const LEADER_ROLES = ["leader", "co-leader"];

const fullName = (person) => [person?.firstName, person?.lastName].filter(Boolean).join(" ");

const DetailsSkeleton = () => (
  <PageContainer>
    <div className="flex items-center gap-3" aria-busy="true">
      <Skeleton className="h-10 w-10 rounded-control" />
      <Skeleton className="h-12 w-12 rounded-control" />
      <Skeleton lines={2} className="w-64" />
    </div>
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-24 w-full rounded-card" />
      ))}
    </div>
    <Skeleton className="h-72 w-full rounded-card" />
  </PageContainer>
);

const GroupDetails = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { id } = useParams();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingMember, setRemovingMember] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchGroup = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await groupsService.getGroupById(id);
      if (response.success) {
        setGroup(response.data);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  useEffect(() => {
    if (group?.id) {
      track(EVENTS.GROUP_VIEWED, { group_id: group.id, group_name: group.name });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id]);

  const handleRemoveMember = async (userId, userName) => {
    const confirmed = await confirm({
      title: `Remove "${userName}"?`,
      message: `${userName} will be removed from ${group.name}.`,
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      setRemovingMember(userId);
      await groupsService.removeMember(id, userId);
      notify.success("Member removed");
      await fetchGroup();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not remove member"));
    } finally {
      setRemovingMember(null);
    }
  };

  const handleDeleteGroup = async () => {
    const confirmed = await confirm({
      title: `Delete "${group.name}"?`,
      message: "This group and its member list will be permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      setDeleting(true);
      await groupsService.deleteGroup(id);
      notify.success("Group deleted");
      navigate("/admin/groups");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete group"));
      setDeleting(false);
    }
  };

  if (loading && !group) {
    return <DetailsSkeleton />;
  }

  if (error || !group) {
    return (
      <PageContainer>
        <PageHeader title="Group Details" backTo="/admin/groups" backLabel="Back to Groups" />
        <ErrorState
          message={getApiErrorMessage(error, "We couldn't load this group.")}
          onRetry={fetchGroup}
        />
      </PageContainer>
    );
  }

  const memberships = group.memberships || [];
  const leaderCount = memberships.filter((m) => LEADER_ROLES.includes(m.role)).length;
  const editPath = `/admin/groups/${id}/edit`;

  return (
    <PageContainer>
      <PageHeader
        title={group.name}
        leading={<GroupSwatch group={group} size="lg" />}
        backTo="/admin/groups"
        backLabel="Back to Groups"
        meta={
          <>
            <Badge>{groupTypeLabel(group.groupType)}</Badge>
            {group.isSystem && <Badge tone="info">System Group</Badge>}
          </>
        }
        description={group.description}
        actions={
          <>
            {!group.isSystem && (
              <Button
                variant="secondary"
                leftIcon={<MdDelete className="h-5 w-5" />}
                loading={deleting}
                onClick={handleDeleteGroup}
              >
                Delete
              </Button>
            )}
            <Button as={Link} to={editPath} leftIcon={<MdEdit className="h-5 w-5" />}>
              Edit Group
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        <StatCard
          icon={MdPeople}
          accent="sage"
          label="Total Members"
          value={formatNumber(group.memberCount || 0)}
        />
        <StatCard icon={MdStar} accent="gold" label="Leaders" value={formatNumber(leaderCount)} />
        <StatCard
          icon={MdCheckCircle}
          accent={group.isActive ? "sky" : "danger"}
          label="Status"
          value={group.isActive ? "Active" : "Inactive"}
        />
      </div>

      <Card padding="md" className="gap-5">
        <CardHeader
          title={`Members (${formatNumber(group.memberCount || 0)})`}
          actions={
            memberships.length > 0 && (
              <Button
                as={Link}
                to={editPath}
                variant="secondary"
                size="sm"
                leftIcon={<MdAdd className="h-4 w-4" />}
              >
                Add Members
              </Button>
            )
          }
        />

        {memberships.length === 0 ? (
          <EmptyState
            icon={MdPeople}
            title="No Members Yet"
            description="Add people to this group to start tracking it."
            action={
              <Button as={Link} to={editPath} leftIcon={<MdAdd className="h-5 w-5" />}>
                Add Members
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-line rounded-control border border-line">
            {memberships.map((membership) => {
              const name = fullName(membership.user);
              const isRemoving = removingMember === membership.user.id;
              return (
                <li key={membership.id} className="flex items-center justify-between gap-3 p-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar user={membership.user} size="md" />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-fg">{name}</p>
                        <Badge size="sm">{groupRoleLabel(membership.role)}</Badge>
                        {membership.user.status === "inactive" && (
                          <Badge size="sm" tone={statusTone("inactive")} dot>
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <p className="truncate text-sm text-fg-muted">{membership.user.email}</p>
                      {membership.addedByUser && (
                        <p className="mt-1 text-xs text-fg-muted">
                          Added by {fullName(membership.addedByUser)}
                        </p>
                      )}
                    </div>
                  </div>
                  <IconButton
                    icon={<MdClose className="h-5 w-5" />}
                    label={`Remove ${name}`}
                    variant="secondary"
                    size="sm"
                    loading={isRemoving}
                    onClick={() => handleRemoveMember(membership.user.id, name)}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {group.creator && (
        <Card padding="md" className="gap-4">
          <CardHeader title="Group Information" />
          <dl className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-fg-muted">Created By</dt>
              <dd className="text-sm text-fg">{fullName(group.creator)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-fg-muted">Created</dt>
              <dd className="text-sm text-fg">{formatDate(group.createdAt)}</dd>
            </div>
          </dl>
        </Card>
      )}
    </PageContainer>
  );
};

export default GroupDetails;
